// AppLock SetupScreen — password creation form (shown when !isConfigured).

import { useState } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';

interface SetupScreenProps {
  onSubmit: (password: string) => Promise<void>;
  onSkip: () => void;
}

export function SetupScreen({ onSubmit, onSkip }: SetupScreenProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      await onSubmit(password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Setup failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-6 p-6"
      style={{ backgroundColor: 'var(--color-bg)', color: 'var(--color-text)' }}
      data-testid="applock-setup"
    >
      <div className="panel" style={{ maxWidth: '420px', width: '100%' }}>
        <div className="flex flex-col items-center gap-2" style={{ textAlign: 'center' }}>
          <Lock size={36} aria-hidden="true" style={{ color: 'var(--color-accent)' }} />
          <h1 className="text-xl font-bold">Create Password</h1>
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            This password encrypts your sessions, profiles, and bag data.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4" style={{ marginTop: 'var(--space-5)' }}>
          <div>
            <label htmlFor="applock-password" className="field-label">
              Password
            </label>
            <div className="flex items-center gap-2">
              <input
                id="applock-password"
                type={show ? 'text' : 'password'}
                className="field-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                autoFocus
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="btn"
                onClick={() => setShow((s) => !s)}
                aria-label={show ? 'Hide password' : 'Show password'}
                style={{ padding: 'var(--space-2)' }}
              >
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="applock-confirm" className="field-label">
              Confirm Password
            </label>
            <input
              id="applock-confirm"
              type={show ? 'text' : 'password'}
              className="field-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </div>

          {error && (
            <p
              className="text-sm"
              style={{ color: 'var(--color-danger)' }}
              role="alert"
            >
              {error}
            </p>
          )}

          <p
            className="text-xs"
            style={{ color: 'var(--color-warn)' }}
          >
            No recovery available — if you forget this password, your data cannot be restored.
          </p>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || !password || !confirm}
            data-testid="applock-setup-submit"
          >
            {busy ? 'Encrypting…' : 'Create & Encrypt'}
          </button>
          <button
            type="button"
            className="btn"
            onClick={onSkip}
            disabled={busy}
          >
            Skip
          </button>
        </form>
      </div>
    </div>
  );
}
